/**
 * Ticket Lifecycle v2 — Slice 4C.1; FOH Workboard.
 *
 * Lightweight count hook for the FOH Workboard nav indicator. The badge is
 * the number of LOGICAL repair orders in the Workboard's Needs Attention
 * lane, read through a small shared projection (fetchFohAttentionSummary:
 * the board's own logical-owner + lane rule) rather than the full board.
 *
 * Distinct query key (`['foh-custody-counts', shopId]`) so the page-
 * level queue cache and the nav-indicator cache don't fight each
 * other. Both are invalidated together by
 * `useTicketCustodyRealtime` (slice 4A.2) and by
 * `useTicketCustodyMutation.onSuccess` (slice 3).
 */
import { useQuery } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { FohQueueCounts } from '@/lib/tickets/lifecycle/fohNavStatus';
import { TICKET_UPDATED_EVENT, retainDayboardBroadcastChannel } from '@/utils/dayboardInvalidation';
import { fetchFohAttentionSummary } from '@/hooks/foh/fohAttentionProjection';

export function useFohQueueCounts(
  shopId: string | null | undefined,
  enabled: boolean,
) {
  const qc = useQueryClient();

  // Slice 4C.1: piggy-back on the existing custody-row mutation
  // invalidation. Whenever the slice-3 mutation invalidates the queue
  // page query, also bump the count cache so the nav badge stays in
  // sync without realtime.
  useEffect(() => {
    if (!enabled || !shopId) return;
    const unsubscribe = qc.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated') return;
      const key = event.query.queryKey as unknown[];
      if (key[0] === 'foh-custody-queue' && key[1] === shopId) {
        qc.invalidateQueries({ queryKey: ['foh-custody-counts', shopId] });
      }
    });
    // A pickup changes the TICKET, not the custody row, so custody realtime
    // never fires for it. Follow the existing "a ticket changed" signal
    // (local mutations + the Dayboard's shop broadcast) so a vehicle that
    // just left drops out of the queue and the badge. No new channel.
    const onTicketUpdated = () => {
      qc.invalidateQueries({ queryKey: ['foh-custody-queue', shopId] });
      qc.invalidateQueries({ queryKey: ['foh-custody-counts', shopId] });
    };
    window.addEventListener(TICKET_UPDATED_EVENT, onTicketUpdated);
    // Hear OTHER devices' changes too (e.g. a pickup at the counter) even
    // when no Dashboard is mounted here: the shop's existing dayboard
    // broadcast channel, shared and reference-counted. Its receiver turns a
    // broadcast into the local `ticket-updated` handled above.
    const releaseBroadcast = retainDayboardBroadcastChannel(shopId);
    return () => {
      unsubscribe();
      window.removeEventListener(TICKET_UPDATED_EVENT, onTicketUpdated);
      releaseBroadcast();
    };
  }, [qc, shopId, enabled]);

  return useQuery({
    queryKey: ['foh-custody-counts', shopId],
    enabled: enabled && !!shopId,
    retry: false,
    queryFn: async (): Promise<FohQueueCounts> => {
      try {
        return await fetchFohAttentionSummary(shopId as string);
      } catch (error) {
        console.error('[foh-counts] failed to read the FOH attention projection', error);
        throw error;
      }
    },
  });
}
